import { Builder, Input } from '@builder.io/sdk';
import { LandingPageForm } from './LandingPageForm';
import { BuilderInputOptions } from '@/types/builder';
import { DURATION_OPTIONS } from '@/domain/duration';

export const journeyInput = {
  name: 'journey',
  helperText: 'Which journey does this form need to link to?',
  type: 'enum',
  defaultValue: 'none',
  enum: [
    {
      label: 'No specific journey',
      value: 'none',
    },
    {
      label: 'Learner',
      value: 'learner',
    },
    {
      label: 'Expat',
      value: 'expat',
    },
    {
      label: 'Impounded',
      value: 'impounded',
    },
    {
      label: 'Courier',
      value: 'courier',
    },
    {
      label: 'Motorbike',
      value: 'motorbike',
    },
  ],
};

export const showDurationPickerInput = {
  name: 'showDurationPicker',
  friendlyName: 'Show duration picker',
  helperText: 'Show or hide the duration picker module',
  type: 'boolean',
  defaultValue: true,
};

export const landingPageFormInputs = [
  {
    name: 'heading',
    type: 'string',
    friendlyName: 'Heading',
    helperText: 'Main heading displayed above the form',
    required: false,
  },
  {
    name: 'subHeading',
    type: 'string',
    friendlyName: 'Subheading',
    helperText: 'Supporting text displayed below the heading',
    required: false,
  },
  {
    name: 'useCardStyle',
    friendlyName: 'Use card style',
    type: 'boolean',
    helperText: 'Display the form in a card container',
    defaultValue: false,
  },
  {
    name: 'regLabel',
    friendlyName: 'Input label',
    type: 'string',
    defaultValue: 'To get started, tell us the reg of the car or van you want cover for',
  },
  {
    name: 'showRegLabel',
    friendlyName: 'Show Registration Label',
    type: 'boolean',
    helperText: 'Show or hide the label above the registration input',
    defaultValue: true,
  },
  {
    name: 'showRegToggle',
    friendlyName: 'Show "I don\'t know my reg" Toggle',
    type: 'boolean',
    helperText: 'Show or hide the "I don\'t know my reg yet" toggle link',
    defaultValue: true,
  },
  {
    name: 'useBanner',
    friendlyName: 'Use banner',
    type: 'boolean',
    defaultValue: false,
  },
  {
    name: 'bannerCoverDuration',
    friendlyName: 'Banner cover duration',
    type: 'string',
    showIf: (options: BuilderInputOptions) => !!options.get('useBanner'),
  },
  {
    name: 'bannerCoverType',
    friendlyName: 'Banner cover type',
    type: 'string',
    showIf: (options: BuilderInputOptions) => !!options.get('useBanner'),
  },
  {
    name: 'checkboxLabel',
    friendlyName: 'Checkbox label',
    type: 'string',
    showIf: (options: BuilderInputOptions) => !!options.get('useBanner'),
  },
  showDurationPickerInput,
  {
    name: 'defaultDurationType',
    friendlyName: 'Default duration',
    type: 'enum',
    // Deliberately no defaultValue. A registry default is applied to every page
    // that never set the field, which would make every page pass a duration —
    // including the pages that imply none and should leave the choice to the
    // quote journey. See @/domain/duration.
    helperText:
      'Only set this on pages that imply a specific duration (e.g. /one-day). Leave as "None" on general pages so the quote journey applies its own default.',
    enum: [
      { label: 'None (use quote journey default)', value: '' },
      ...DURATION_OPTIONS.map((value) => ({ label: value, value })),
    ],
    // Intentionally always shown. It used to be hidden whenever the duration
    // picker was off, which is precisely when it is the only thing determining
    // the duration passed on — so editors could not set it where it mattered.
  },
  {
    name: 'daysOnly',
    helperText: 'Limit selection to days only.',
    type: 'boolean',
    defaultValue: false,
    showIf: (options: BuilderInputOptions) => !options.get('useBanner') && options.get('showDurationPicker') !== false,
  },
  journeyInput,
  {
    name: 'regPlateOnly',
    helperText: 'Select this option if all you need is a reg plate input',
    type: 'boolean',
    defaultValue: false,
  },
  {
    name: 'formIntroText',
    friendlyName: 'Form Introduction Text',
    type: 'string',
    helperText: 'Text shown above the registration input field',
    defaultValue: 'Or to get started simply enter your reg...',
    required: false,
  },
  {
    name: 'submitButtonText',
    friendlyName: 'Submit Button Text',
    type: 'string',
    helperText: 'Text displayed on the submit button',
    defaultValue: 'Continue',
    required: false,
  },
  {
    name: 'submitButtonColor',
    friendlyName: 'Submit Button Color',
    type: 'enum',
    helperText: 'Choose the background color for the submit button',
    defaultValue: 'orange',
    enum: [
      {
        label: 'Orange (Primary)',
        value: 'orange',
      },
      {
        label: 'Purple (Secondary)',
        value: 'purple',
      },
    ],
    required: false,
  },
];

export const getFilteredFormInputs = ({
  excludedFields = [],
  defaultOverrides = {},
  gateOnEnableForm = false,
}: {
  excludedFields?: string[];
  defaultOverrides?: Record<string, string | boolean>;
  gateOnEnableForm?: boolean;
} = {}): Input[] => {
  const overrideDefaults = (input: Input): Input => {
    if (input.name in defaultOverrides) {
      return { ...input, defaultValue: defaultOverrides[input.name] };
    }
    return input;
  };

  const applyEnableFormGate = (input: Input): Input => {
    const existingShowIf = input.showIf as ((options: BuilderInputOptions) => boolean) | undefined;
    return {
      ...input,
      showIf: existingShowIf
        ? (options: BuilderInputOptions) => !!options?.get('enableForm') && existingShowIf(options)
        : (options: BuilderInputOptions) => !!options?.get('enableForm'),
    };
  };

  const filtered = landingPageFormInputs.filter((input) => !excludedFields.includes(input.name)).map(overrideDefaults);

  return gateOnEnableForm ? filtered.map(applyEnableFormGate) : filtered;
};

const LandingPageFormConfig = {
  name: 'Landingpage Form',
  inputs: landingPageFormInputs,
};

Builder.registerComponent(LandingPageForm, LandingPageFormConfig);
